import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { MyDialog } from 'components/MyDialog'
import { Pill } from 'components/Pill/Pill'
import { MyTextField } from 'components/ReactHookForm/MyTextField'
import { UsernameGuidelines } from './UsernameGuidelines'
import { ConfirmPasswordForm } from '../PasswordForm/ConfirmPasswordForm'
import { createSchema, EditUsernameFormData } from 'models/forms/UsernameForm'
import { UpdateAccountServerErrors } from 'hooks/useUpdateAccountHandler'
import { UpdateAccountModalErrors } from '../UpdateAccountModalErrors'
import { UpdateAccountModalActions } from '../UpdateAccountModalActions'

export type UsernameModalProps = {
  loading?: boolean
  onClose: () => void
  onEnterPwd: (password: string) => void
  onSubmit: (formData: EditUsernameFormData) => void
  username: string
  password: string
  serverErrors: UpdateAccountServerErrors
}
export const UsernameModal = ({
  loading = false,
  username,
  onClose,
  onEnterPwd,
  onSubmit,
  password,
  serverErrors
}: UsernameModalProps) => {
  const { t } = useTranslation('editUsername')

  const schema = useMemo(() => createSchema(t), [t])
  const methods = useForm<EditUsernameFormData>({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: zodResolver(schema)
  })

  return (
    <MyDialog
      heading={t('heading')}
      containerId="medicare-my-acct-username-settings"
      closeButtonAriaLabel={t('Close')}
      onClose={onClose}
    >
      <>
      <p>{t('verifyText')}</p>
      {!password
        ? (
        <ConfirmPasswordForm
          onSubmit={onEnterPwd}
          onClose={onClose}
        />
          )
        : (
        <>
          <div className="my-u-mb--14">
            <h2 className="ds-text-heading--xl">{t('currentValueHeading')}</h2>
            <Pill>{username}</Pill>
          </div>
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <fieldset className="ds-c-fieldset">
                <legend className="ds-c-label">
                  <h2 className="ds-text-heading--xl">{t(`createNewHeading`)}</h2>
                </legend>
                <MyTextField
                  name="username"
                  label={t(`username.label`)}
                  labelClassName="my-u-text-transform--default"
                  defaultValue=""
                />
              </fieldset>

              <UsernameGuidelines className="my-u-mt--14" />

              <UpdateAccountModalErrors
                serverErrors={serverErrors}
                basicErrorText={t('errors.server-basic')}
                invalidCriteriaErrorText={t('errors.server-validation')}
              />

              <UpdateAccountModalActions
                loading={loading}
                submitButtonText={t('submit')}
                submitSiteActivityProps={{
                  core: {
                    component: 'Account Settings',
                    elementId: 'accountsettings_usernamesave_click',
                    coreClickData: {
                      linkText: t('submit')
                    }
                  }
                }}
                cancelButtonText={t('cancel')}
                handleCancel={onClose}
              />

            </form>
          </FormProvider>
        </>
          )}
      </>
    </MyDialog>
  )
}
